/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { OrderRiskAnalysisResultProps, Predictions, RiskLevel } from './types';
import { useMutation } from '@apollo/client';
import { FETCH_CUSTOMER_ANALYTICS } from 'gql/mutations';
import { withNotification } from 'components/Notification';
import { capitalize } from 'utils';
import { useStore } from 'context/store-context';
import { Modal } from 'components/Modal';
import { Text } from 'components/Text';

import {
  ShoppingBagIcon,
  ReceiptRefundIcon,
  ClockIcon,
  TagIcon,
  CurrencyDollarIcon,
  ChartPieIcon,
  TrashIcon,
  CheckCircleIcon,
} from '@heroicons/react/solid';
import { DocumentTextIcon } from '@heroicons/react/outline';
interface IAnalyticsModalProps {
  isOpen: boolean;
  onClose: (value: boolean) => void;
  analytics: {
    orders?: {
      days_since_last_order: number;
      first_order_date: string;
      first_order_id: string;
      last_order_date: string;
      last_order_id: string;
      last_order_name: string;
      num_items: number;
      num_orders: number;
      num_orders_fulfilled: number;
      num_orders_subscription: number;
      num_titles: number;
      number_of_offline_orders: number;
      number_of_online_orders: number;
      recent_num_orders: number;
      updated_at: string;
    };
    refunds?: {
      fulfilled_and_refund_pct: number;
      item_fulfilled_and_refund_pct: number;
      item_num_fulfilled_and_refunds: number;
      item_num_refunds: number;
      item_refund_pct: number;
      num_fulfilled_and_refunds: number;
      num_refunds: number;
      refund_pct: number;
      refund_signatures_detail: any[];
      total_num_fulfilled_items: number;
      total_num_items: number;
      total_refunds: number;
      updated_at: string;
    };
  };
}

const RiskBadge = ({ level }: { level: RiskLevel }) => {
  const getColors = () => {
    switch (level.toLowerCase()) {
      case 'high':
        return 'bg-gradient-to-r from-red-500 to-red-600 text-white';
      case 'medium':
        return 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-800';
      case 'low':
      default:
        return 'bg-gradient-to-r from-green-400 to-green-500 text-white';
    }
  };

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getColors()}`}
    >
      {level.toUpperCase()}
    </span>
  );
};

const Icon = ({ type }: { type: 'risk' | 'trust' }) => {
  if (type === 'risk') {
    return (
      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-50 mr-3">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-red-500"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-50 mr-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-green-500"
      >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    </div>
  );
};

const AnalyticsModal = ({
  isOpen,
  onClose,
  analytics,
}: IAnalyticsModalProps) => {
  if (!analytics) return null;
  return (
    <Modal
      openModal={(value) => onClose(!value)}
      open={isOpen}
      containerClass="sm:max-w-4xl sm:w-full"
    >
      <div className="relative">
        {/* Decorative top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-400"></div>

        {/* Header with improved typography and spacing */}
        <div className="border-b border-gray-200 pb-6 mb-8 pt-4">
          <div className="flex items-center">
            <div className="bg-indigo-100 p-2 rounded-lg mr-3">
              <svg
                className="w-6 h-6 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div>
              <Text
                value="Customer Analytics Dashboard"
                type="h4"
                className="text-gray-900 font-semibold"
              />
              <Text
                value="Detailed insights into customer order and refund behavior"
                type="sm"
                className="text-gray-500 mt-1"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Order Details */}
          <div className="space-y-5">
            <Text
              value="Order Details"
              type="h4"
              className="text-gray-800 font-medium flex items-center"
            >
              <div className="bg-blue-100 p-1.5 rounded-md mr-2">
                <ShoppingBagIcon className="w-4 h-4 text-blue-600" />
              </div>
              Order Details
            </Text>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                <div className="flex items-start">
                  <div className="bg-blue-50 p-2 rounded-lg mr-3">
                    <ShoppingBagIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <Text
                      value="Total Orders"
                      type="sm"
                      className="text-gray-500 font-medium"
                    />
                    <Text
                      value={analytics.orders?.num_orders || 0}
                      type="h3"
                      className="text-gray-900 font-bold mt-1"
                    />
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-50 p-2 rounded-lg mr-3">
                    <TagIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <Text
                      value="Total Items"
                      type="sm"
                      className="text-gray-500 font-medium"
                    />
                    <Text
                      value={analytics.orders?.num_items || 0}
                      type="h3"
                      className="text-gray-900 font-bold mt-1"
                    />
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-50 p-2 rounded-lg mr-3">
                    <ClockIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <Text
                      value="Days Since Last"
                      type="sm"
                      className="text-gray-500 font-medium"
                    />
                    <Text
                      value={analytics.orders?.days_since_last_order || 0}
                      type="h3"
                      className="text-gray-900 font-bold mt-1"
                    />
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-50 p-2 rounded-lg mr-3">
                    <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <Text
                      value="Unique Titles"
                      type="sm"
                      className="text-gray-500 font-medium"
                    />
                    <Text
                      value={analytics.orders?.num_titles || 0}
                      type="h3"
                      className="text-gray-900 font-bold mt-1"
                    />
                  </div>
                </div>
              </div>

              {analytics.orders?.last_order_date && (
                <div className="flex border-gray-100 items-center mt-2">
                  <ClockIcon className="w-4 h-4 text-gray-400 mr-1.5" />
                  {`Last Order: ${new Date(
                    analytics.orders.last_order_date,
                  ).toLocaleDateString()}`}
                </div>
              )}
            </div>
          </div>

          {/* Refund Details */}
          <div className="space-y-5">
            <Text
              value="Refund Details"
              type="h4"
              className="text-gray-800 font-medium flex items-center"
            >
              <div className="bg-red-100 p-1.5 rounded-md mr-2">
                <ReceiptRefundIcon className="w-4 h-4 text-red-600" />
              </div>
              Refund Details
            </Text>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                <div className="flex items-start">
                  <div className="bg-red-50 p-2 rounded-lg mr-3">
                    <CurrencyDollarIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <Text
                      value="Total Refunds"
                      type="sm"
                      className="text-gray-500 font-medium"
                    />
                    <Text
                      value={analytics.refunds?.total_refunds || 0}
                      type="h3"
                      className="text-gray-900 font-bold mt-1"
                    />
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-red-50 p-2 rounded-lg mr-3">
                    <ChartPieIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <Text
                      value="Refund Rate"
                      type="sm"
                      className="text-gray-500 font-medium"
                    />
                    <Text
                      value={`${(
                        (analytics.refunds?.refund_pct || 0) * 100
                      ).toFixed(1)}%`}
                      type="h3"
                      className="text-gray-900 font-bold mt-1"
                    />
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-red-50 p-2 rounded-lg mr-3">
                    <TrashIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <Text
                      value="Items Refunded"
                      type="sm"
                      className="text-gray-500 font-medium"
                    />
                    <Text
                      value={analytics.refunds?.item_num_refunds || 0}
                      type="h3"
                      className="text-gray-900 font-bold mt-1"
                    />
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-red-50 p-2 rounded-lg mr-3">
                    <CheckCircleIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <Text
                      value="Fulfilled & Refunded"
                      type="sm"
                      className="text-gray-500 font-medium whitespace-nowrap"
                    />
                    <Text
                      value={analytics.refunds?.num_fulfilled_and_refunds || 0}
                      type="h3"
                      className="text-gray-900 font-bold mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const getBorderColor = (riskLevel: RiskLevel) => {
  switch (riskLevel) {
    case 'high':
      return 'border-red-200';
    case 'medium':
      return 'border-yellow-200';
    case 'low':
      return 'border-green-200';
    default:
      return 'border-gray-200';
  }
};

const getSeverityText = (prediction: Predictions) => {
  if (prediction.risk_level === 'high') return 'High risk detected';
  if (prediction.risk_level === 'medium') return 'Medium risk detected';
  return 'Low risk detected';
};

const OrderRiskAnalysisResult = ({
  orderId,
  flex = 'row',
}: OrderRiskAnalysisResultProps & {
  showNotification: (...params: string[]) => void;
}) => {
  const { storeId } = useStore();
  const [prediction, setPrediction] = useState<Predictions>({
    risk_type: 'return_abuse',
    risk_score: 0,
    risk_level: 'low',
    explanation: '',
  });
  const [analytics, setAnalytics] = useState<any>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [fetchCustomerAnalytics, { loading }] = useMutation(
    FETCH_CUSTOMER_ANALYTICS,
    {
      onCompleted(data) {
        const analytics = data?.fetchCustomerAnalysis?.attributes?.analytics;
        const predictions =
          data?.fetchCustomerAnalysis?.attributes?.predictions || [];

        if (analytics) {
          setAnalytics(analytics);
        }

        if (predictions && predictions.length) {
          setPrediction(predictions[0]);
        }
      },
    },
  );

  useEffect(() => {
    fetchCustomerAnalytics({
      variables: {
        input: { orderId, storeId },
      },
    });
  }, [fetchCustomerAnalytics, orderId, storeId]);

  if (loading) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
        <svg
          className="animate-spin h-6 w-6 text-gray-400 mx-auto mb-3"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8z"
          ></path>
        </svg>
        <p className="text-gray-500 font-medium">Loading risk predictions...</p>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto mb-6 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center md:flex-col flex-row">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-2 text-gray-600"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Risk Analysis
          </h2>
          <div className="text-sm font-medium text-gray-600">
            {prediction && getSeverityText(prediction)}
          </div>
        </div>
      </div>

      <div
        className={`flex p-5 gap-4 ${flex === 'col' ? 'flex-col' : 'flex-row'}`}
      >
        {prediction && (
          <div
            className={`flex-1 border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 ${getBorderColor(
              prediction.risk_level as RiskLevel,
            )}`}
          >
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white">
              <h3 className="font-medium text-gray-800 flex items-center">
                {prediction.risk_level === 'high' && (
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                )}
                {prediction.risk_level === 'medium' && (
                  <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                )}
                {prediction.risk_level === 'low' && (
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                )}

                <p className="text-sm text-gray-700">Risk Level</p>
              </h3>
              <RiskBadge level={prediction.risk_level as RiskLevel} />
            </div>

            <div className="p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm text-gray-600">Risk Score</p>
                <p className="text-sm text-gray-600">{prediction.risk_score}</p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">Risk Type</p>
                <p className="text-sm text-gray-600">
                  {capitalize((prediction.risk_type as string) || '')
                    .split('_')
                    .join(' ')}
                </p>
              </div>

              {prediction.explanation && (
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Risk Explanation
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center bg-white p-2 rounded-md border border-gray-100">
                      <Icon
                        type={
                          ['high', 'medium'].includes(
                            prediction.risk_level as string,
                          )
                            ? 'risk'
                            : 'trust'
                        }
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {prediction.explanation}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {typeof analytics?.orders?.num_orders !== 'undefined' ? (
          <div className="flex-1 border rounded-lg p-4 bg-white shadow-sm">
            <div className="flex justify-between items-center">
              <h3 className="font-medium text-gray-800">Customer Analysis</h3>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Total Orders</p>
                <p className="text-lg font-semibold text-green-600">
                  {analytics.orders.num_orders || 1}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Items</p>
                <p className="text-lg font-semibold text-green-600">
                  {analytics.orders.num_items}
                </p>
              </div>
              <div className="flex justify-start">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View More
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-400 mx-auto mb-3"
            >
              <path d="M12 2a10 10 0 0 0 10 10A10 10 0 0 0 12 2zm0 14a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm1-4a1 1 0 0 1-2 0V7a1 1 0 0 1 2 0z" />
            </svg>
            <p className="text-gray-500 font-medium">
              No historical analysis available for this customer!
            </p>
          </div>
        )}
      </div>

      {analytics && (
        <AnalyticsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          analytics={analytics}
        />
      )}
    </div>
  );
};

export default withNotification(OrderRiskAnalysisResult);
